import { supabase } from '@/integrations/supabase/client';
import {
  esErrorColumnaInexistente,
  esErrorRolNoAsignado,
  esErrorRpcInexistente,
  normalizarRoles,
  type RolAsignado,
} from './multiRol';

/**
 * Roles asignados al usuario autenticado: filas activas de `user_roles` (RLS:
 * cada usuario lee las suyas) unidas con el rol activo, con su nombre de
 * `roles`. Un error de lectura no rompe el login: devuelve solo el rol activo.
 */
export async function obtenerRolesPropios(
  email: string,
  rolActivo: number | null | undefined,
): Promise<RolAsignado[]> {
  const emailLower = email.trim().toLowerCase();

  // `id_persona_contexto` (persona por rol) puede no existir aún en la BD: si el
  // select falla por columna inexistente se reintenta sin ella.
  const leer = (columnas: string) =>
    (supabase as any)
      .from('user_roles')
      .select(columnas)
      .eq('email', emailLower)
      .eq('activo', true) as Promise<{
      data: { rol_id: number; es_principal: boolean | null; id_persona_contexto?: number | null }[] | null;
      error: { code?: string; message?: string } | null;
    }>;
  let { data: filas, error } = await leer('rol_id, es_principal, id_persona_contexto');
  if (error && esErrorColumnaInexistente(error)) {
    ({ data: filas, error } = await leer('rol_id, es_principal'));
  }
  if (error) console.warn('[multi-rol] No se pudieron leer los roles del usuario:', error.message);

  const base = ((error ? [] : filas) ?? []).map((f) => ({
    rol_id: Number(f.rol_id),
    es_principal: !!f.es_principal,
    id_persona_contexto: f.id_persona_contexto != null ? Number(f.id_persona_contexto) : null,
  }));

  const ids = [...new Set([...base.map((b) => b.rol_id), ...(rolActivo != null ? [rolActivo] : [])])];
  const nombres = new Map<number, string>();
  if (ids.length > 0) {
    const { data: roles } = await supabase.from('roles').select('id, nombre').in('id', ids);
    for (const r of roles ?? []) nombres.set(Number(r.id), r.nombre ?? '');
  }

  // Nombre de la empresa que representa cada rol con contexto. Si RLS no deja
  // leer la persona, la etiqueta cae a "persona #id" (ver etiquetaRol).
  const idsContexto = [...new Set(base.map((b) => b.id_persona_contexto).filter((x): x is number => x != null))];
  const nombresContexto = new Map<number, string>();
  if (idsContexto.length > 0) {
    const { data: personas, error: errPersonas } = await supabase
      .from('personas')
      .select('id, nombre_legal, nombre_comercial')
      .in('id', idsContexto);
    if (errPersonas) console.warn('[multi-rol] No se pudo leer la persona de contexto del rol:', errPersonas.message);
    for (const p of personas ?? []) {
      const nombre = (p.nombre_comercial || p.nombre_legal || '').trim();
      if (nombre) nombresContexto.set(Number(p.id), nombre);
    }
  }

  return normalizarRoles(
    base.map((b) => ({
      ...b,
      nombre: nombres.get(b.rol_id) || `Rol ${b.rol_id}`,
      persona_contexto_nombre: b.id_persona_contexto != null ? nombresContexto.get(b.id_persona_contexto) ?? null : null,
    })),
    rolActivo,
    rolActivo != null ? nombres.get(rolActivo) : null,
  );
}

export type ResultadoActivacionRol =
  | { estado: 'ok'; rolActivo: number }
  /** La RPC no existe todavía en esta BD: seguir con el comportamiento de siempre. */
  | { estado: 'sin_rpc' }
  /** 42501: el rol no está activo en `user_roles` para este usuario. */
  | { estado: 'sin_permiso'; mensaje: string }
  | { estado: 'error'; mensaje: string };

/**
 * Cambia el rol ACTIVO de la sesión (`usuarios.rol_id`) vía la RPC
 * `seleccionar_rol_activo`. Nunca hacer `UPDATE usuarios SET rol_id` desde el
 * cliente: el trigger `usuarios_bloquea_autoescalada` lo rechaza.
 * Solo toca BD; quien llama refresca el perfil (ver useCambiarRolActivo).
 */
export async function seleccionarRolActivo(rolId: number): Promise<ResultadoActivacionRol> {
  try {
    // La RPC aún no está en los tipos generados (DDL pendiente en las BDs).
    const { data, error } = await (supabase as any).rpc('seleccionar_rol_activo', { p_rol_id: rolId });
    if (error) {
      if (esErrorRpcInexistente(error)) {
        console.warn(
          '[multi-rol] La RPC seleccionar_rol_activo no existe en esta BD (DDL pendiente); se conserva el rol actual.',
        );
        return { estado: 'sin_rpc' };
      }
      if (esErrorRolNoAsignado(error)) {
        return {
          estado: 'sin_permiso',
          mensaje: 'No tienes asignado ese rol. Pide a un administrador que te lo asigne.',
        };
      }
      return { estado: 'error', mensaje: error.message || 'No se pudo cambiar de rol.' };
    }
    return { estado: 'ok', rolActivo: typeof data === 'number' ? data : rolId };
  } catch (err) {
    return { estado: 'error', mensaje: (err as Error)?.message || 'No se pudo cambiar de rol.' };
  }
}
